import type { Practitioner, Reference } from '@medplum/fhirtypes'
import type {
  ResourceChangeSet,
  ResourceDecision,
} from '@seen/core/queued-edits/model'
import { now } from '@seen/core/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface QueuedEditDecisionsState {
  decisions: Record<string, ResourceDecision>
  // Who recorded each decision, keyed by ResourceChangeSet.id. Drives the
  // decider avatar and the override-confirmation prompt.
  deciders: Record<string, Reference<Practitioner>>
  // When each decision was recorded (ISO), keyed by ResourceChangeSet.id.
  decidedAt: Record<string, string>
  approvedCount: number
  rejectedCount: number
  pendingCount: number
  total: number
  allReviewed: boolean
  setDecision: (id: string, decision: ResourceDecision) => void
  // Record one decision for several changes at once (a group or the whole
  // set), merging into existing decisions. Fires onBulkChange with the
  // resulting full decision map.
  setMany: (ids: string[], decision: ResourceDecision) => void
  // Clear any decisions for several changes at once (a group toggle-off).
  // Fires onBulkChange with the resulting full decision map.
  clearMany: (ids: string[]) => void
}

export interface UseQueuedEditDecisionsOptions {
  // Decisions to seed state with — rehydrates a review interrupted mid-way
  // (read back from the task's recorded outputs).
  initialDecisions?: Record<string, ResourceDecision>
  // Who recorded each seeded decision, keyed by ResourceChangeSet.id.
  initialDeciders?: Record<string, Reference<Practitioner>>
  // When each seeded decision was recorded (ISO), keyed by ResourceChangeSet.id.
  initialDecidedAt?: Record<string, string>
  // The practitioner attributed to decisions made in this session.
  currentDecider?: Reference<Practitioner>
  // Fired whenever a single decision changes, so a host can persist it.
  // `decision` is undefined when the reviewer toggled the decision off.
  onDecisionChange?: (
    id: string,
    decision: ResourceDecision | undefined
  ) => void
  // Fired on bulk approve/reject, with the resulting full decision map.
  onBulkChange?: (decisions: Record<string, ResourceDecision>) => void
}

// Per-resource decision state shared by the inbox, chat, and modal surfaces.
// Decisions are keyed by ResourceChangeSet.id.
export function useQueuedEditDecisions(
  changes: ResourceChangeSet[],
  options: UseQueuedEditDecisionsOptions = {}
): QueuedEditDecisionsState {
  const {
    initialDecisions,
    initialDeciders,
    initialDecidedAt,
    currentDecider,
    onDecisionChange,
    onBulkChange,
  } = options
  const [decisions, setDecisions] = useState<Record<string, ResourceDecision>>(
    initialDecisions ?? {}
  )
  const [deciders, setDeciders] = useState<
    Record<string, Reference<Practitioner>>
  >(initialDeciders ?? {})
  const [decidedAt, setDecidedAt] = useState<Record<string, string>>(
    initialDecidedAt ?? {}
  )

  // Write-through mirror of the committed decisions. The bulk setters read and
  // then immediately update it so they merge into the latest decisions — even
  // when several fire in one render batch, before React re-renders — and can
  // hand onBulkChange the same map they commit.
  const decisionsRef = useRef(decisions)
  decisionsRef.current = decisions

  // `initialDecisions` is usually empty on first render and only fills in once
  // the proposed bundle + recorded outputs have loaded (async). useState seeds
  // once, so hydrate the first time real decisions arrive — but never clobber
  // edits the reviewer has already made this session.
  const hydratedRef = useRef(Object.keys(initialDecisions ?? {}).length > 0)
  const initialKey = initialDecisions
    ? Object.entries(initialDecisions)
        .map(([id, d]) => `${id}:${d}`)
        .sort()
        .join('|')
    : ''
  useEffect(() => {
    if (
      hydratedRef.current ||
      !initialDecisions ||
      Object.keys(initialDecisions).length === 0
    ) {
      return
    }
    hydratedRef.current = true
    setDecisions((prev) =>
      Object.keys(prev).length > 0 ? prev : initialDecisions
    )
    setDeciders((prev) =>
      Object.keys(prev).length > 0 ? prev : (initialDeciders ?? {})
    )
    setDecidedAt((prev) =>
      Object.keys(prev).length > 0 ? prev : (initialDecidedAt ?? {})
    )
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- initialKey is a content hash of initialDecisions that must drive hydration; the maps are read via closure
  }, [initialKey])

  const setDecision = useCallback(
    (id: string, decision: ResourceDecision) => {
      const at = now()
      // decisionsRef is the authoritative, immediately-updated view so setters
      // batched in one render read each other's results (React re-renders once,
      // after the batch). Toggle off when the same decision is clicked again.
      const cleared = decisionsRef.current[id] === decision
      const next = cleared
        ? Object.fromEntries(
            Object.entries(decisionsRef.current).filter(([k]) => k !== id)
          )
        : { ...decisionsRef.current, [id]: decision }
      decisionsRef.current = next
      setDecisions(next)
      const dropKey = <V>(map: Record<string, V>) =>
        Object.fromEntries(Object.entries(map).filter(([k]) => k !== id))
      setDeciders((prev) =>
        cleared
          ? dropKey(prev)
          : currentDecider
            ? { ...prev, [id]: currentDecider }
            : prev
      )
      setDecidedAt((prev) => (cleared ? dropKey(prev) : { ...prev, [id]: at }))
      onDecisionChange?.(id, cleared ? undefined : decision)
    },
    [currentDecider, onDecisionChange]
  )

  const setMany = useCallback(
    (ids: string[], decision: ResourceDecision) => {
      const at = now()
      const next = {
        ...decisionsRef.current,
        ...Object.fromEntries(ids.map((id) => [id, decision])),
      }
      decisionsRef.current = next
      setDecisions(next)
      setDeciders((prev) =>
        currentDecider
          ? {
              ...prev,
              ...Object.fromEntries(ids.map((id) => [id, currentDecider])),
            }
          : prev
      )
      setDecidedAt((prev) => ({
        ...prev,
        ...Object.fromEntries(ids.map((id) => [id, at])),
      }))
      onBulkChange?.(next)
    },
    [currentDecider, onBulkChange]
  )

  const clearMany = useCallback(
    (ids: string[]) => {
      const drop = new Set(ids)
      const dropKeys = <TValue>(
        map: Record<string, TValue>
      ): Record<string, TValue> =>
        Object.fromEntries(Object.entries(map).filter(([k]) => !drop.has(k)))
      const next = dropKeys(decisionsRef.current)
      decisionsRef.current = next
      setDecisions(next)
      setDeciders((prev) => dropKeys(prev))
      setDecidedAt((prev) => dropKeys(prev))
      onBulkChange?.(next)
    },
    [onBulkChange]
  )

  return useMemo(() => {
    const values = changes.map((c) => decisions[c.id])
    const approvedCount = values.filter((d) => d === 'approved').length
    const rejectedCount = values.filter((d) => d === 'rejected').length
    const total = changes.length
    const pendingCount = total - approvedCount - rejectedCount
    return {
      decisions,
      deciders,
      decidedAt,
      approvedCount,
      rejectedCount,
      pendingCount,
      total,
      allReviewed: pendingCount === 0 && total > 0,
      setDecision,
      setMany,
      clearMany,
    }
  }, [changes, decisions, deciders, decidedAt, setDecision, setMany, clearMany])
}
